import React, { useCallback, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';
import { VideoCard, VideoPlayDialog, SegmentManageDialog } from '@/components/seller/live/LiveProductVideos';

const field = 'px-2 py-1.5 rounded border border-black/15 dark:border-white/20 bg-transparent text-sm';

// LIVE-RECORDING-VIDEO-LIBRARY-1 · 전체 상품 소개영상 · 기존 VideoCard·재생·관리 다이얼로그 REUSE (대표 변경은 상품관리에서).
export const ProductVideoLibrary = ({ scheduleId, onClearSchedule }) => {
  const [f, setF] = useState({ q: '', status: '', date_from: '', date_to: '' });
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [play, setPlay] = useState(null);
  const [manage, setManage] = useState(null);
  const load = useCallback((p) => {
    const params = { page: p, page_size: 20 };
    ['q', 'status', 'date_from', 'date_to'].forEach((k) => { if (f[k]) params[k] = f[k]; });
    if (scheduleId) params.schedule_id = scheduleId;
    api.get('/seller/product-videos-library', { params })
      .then(({ data: d }) => setData((prev) => (p > 1 && prev ? { ...d, videos: [...prev.videos, ...d.videos] } : d)))
      .catch(() => setData({ videos: [], total: 0 }));
  }, [f, scheduleId]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(1); }, 300); return () => clearTimeout(t); }, [load]);
  const reload = () => { setManage(null); setPage(1); load(1); };
  return (
    <div className="space-y-3" data-testid="product-video-library">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[160px]">
          <Search size={14} className="absolute left-2 top-2.5 text-gray-400" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="상품명·방송명 검색" className={`${field} w-full pl-7`} data-testid="pv-library-search" />
        </div>
        <input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className={field} aria-label="시작일" data-testid="pv-library-date-from" />
        <input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className={field} aria-label="종료일" data-testid="pv-library-date-to" />
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={field} data-testid="pv-library-status">
          <option value="">전체 상태</option><option value="ready">재생 가능</option><option value="stale">구간 수정됨</option>
          <option value="processing">생성 중</option><option value="failed">실패</option><option value="hidden">소비자 숨김</option>
        </select>
      </div>
      {scheduleId && (
        <p className="text-xs" data-testid="pv-library-schedule-filter">방송 필터 적용 중 · <button type="button" onClick={onClearSchedule} className="text-[#FF3B30] font-bold" data-testid="pv-library-clear-schedule">전체 보기</button></p>
      )}
      <p className="text-[11px] text-gray-500">같은 상품을 다시 소개하면 카드가 따로 표시되고, 같은 소개 구간의 수정본(v1·v2)은 한 카드의 보관 버전으로 표시됩니다. 대표영상 변경은 상품관리 → 상품 → 라이브 소개영상에서 할 수 있습니다.</p>
      {!data && <p className="text-xs text-gray-500">불러오는 중…</p>}
      {data && !data.videos.length && <p className="text-sm text-gray-500 py-6 text-center" data-testid="pv-library-empty">조건에 맞는 상품 소개영상이 없습니다.</p>}
      <div className="grid gap-2 md:grid-cols-2">
        {(data?.videos || []).map((v) => (
          <div key={v.segment_id} className="space-y-1" data-testid={`pv-library-item-${v.segment_id}`}>
            <div className="flex items-center gap-2 text-xs">
              {v.product_image ? <SafeImg src={v.product_image} alt="" className="w-8 h-8 rounded object-cover" /> : <div className="w-8 h-8 rounded bg-black/5" />}
              <span className="font-bold truncate" data-testid="pv-library-product-name">{v.product_name}</span>
              {v.versions?.length > 1 && <span className="text-gray-500 shrink-0" data-testid="pv-library-versions">버전 {v.versions.map((x) => `v${x.version}`).join('·')}</span>}
            </div>
            <VideoCard v={v} onPlay={setPlay} onManage={setManage} />
          </div>
        ))}
      </div>
      {data?.has_more && <button type="button" onClick={() => { const n = page + 1; setPage(n); load(n); }} className="w-full py-2 rounded border text-xs" data-testid="pv-library-load-more">더 보기 ({data.videos.length}/{data.total})</button>}
      <VideoPlayDialog v={play} onClose={() => setPlay(null)} />
      <SegmentManageDialog v={manage} onClose={() => setManage(null)} onSaved={reload} onPlay={setPlay} />
    </div>
  );
};
